import {
  FsAggregatedAnalytics,
  FsAnalyticsCards,
  RoundsFS,
  GroupFS,
  FsAnalyticsData,
  FSFundMetricsDTO,
  FundStrategyModel,
} from "../../pojo/fund-strategy-export-classes";

export class FsAnalyticsCalcService {

  /**
   * Calculate table data as well as cards analytics.
   *
   * We iterate through all rounds in each subGroup and group and calculate the numbers of companies,
   * which were sold(exit) at gain, loss or at cost.
   *
   * gain = proceeds - investment.
   * gain > 0 company was sold at gain
   * gain < 0 company was sold at loss
   * gain = 0 company was sold at cost
   *
   * | investment | exitComp | proceeds |
   * | 1000       | 1        | 5000     | 1 comp atGain -> 5000 - 1000 > 0                          | inv = 1000       | proceed = 5000
   * | 2000       | 1        | 2200     | 1 comp atLoss -> 2200 - (1000 + 2000) < 0                 | inv = 3000       | proceed = 2200
   * | 3000       | 1        | 6000     | 1 comp atCost -> 6000 - (1000 + 2000 + 3000) = 0          | inv = 6000       | proceed = 6000
   * | 4000       | 2        | 22 000   | 2 comp atGain -> 22 000 - (1000 + 2000 + 3000 + 4000) > 0 | inv = 10 000 * 2 | proceed = 22 000 *2
   *
   * finally we have
   *                  | investment | proceed
   * atGain -> 3 comp | 21 000     | 47 000
   * atLoss -> 1 comp | 3 000      | 2200
   * atCost -> 1 comp | 6000       | 6000
   *
   * ----------------------------------------------------
   * write off means, that we sell the company by 0$.
   * | investment | writeOff |
   * | 1000       | 1        | 1 comp writeOff | inv = 1000
   * | 2000       | 3        | 3 comp writeOff | inv = (1000 + 2000) * 3
   *
   * finally we have
   *                    | investment
   * writeOff -> 4 comp | 10 000
   *
   * ----------------------------------------------------
   * unallocated companies - companies that were neither in exit nor in writeOff (outdated => impossible to save after 06.2025)
   * | companiesNumber | investment  | exit | writeOff |
   * | 5               |  1000       |   2  |  1       | 2 exit, 1 writeOff -> calculations described before
   * | 2               |  2000       |   0  |  0       | 2 companies unallocated -> ignore
   * | 2               |  3000       |   0  |  0       | 2 companies unallocated -> inv = 2 * (1000 + 2000 + 3000)
   *
   * only last round is considered in the calculations for unallocated companies
   *
   * @param groupsFS group data
   */
  static calculateAnalyticsData(groupsFS: GroupFS[]): FsAnalyticsData {
    const analyticsData = new FsAnalyticsData();
    const aggregated = new FsAggregatedAnalytics();

    if (!groupsFS || groupsFS.length === 0) {
      return analyticsData;
    }

    groupsFS.forEach((group) => {
      group.subGroups.forEach((sb) => {
        let rInvestment = 0; // should be reset for each subgroup
        sb.roundsData.sort((a, b) => a.roundOrder - b.roundOrder);

        const subGroupAnalytics = this.calculateSubGroupAnalytics(sb.roundsData);
        aggregated.initialInvestment += subGroupAnalytics.initialInvestment;
        aggregated.followOnInvestment += subGroupAnalytics.followOnInvestment;
        aggregated.totalInvestment += subGroupAnalytics.totalInvestment;

        aggregated.initialRoundsNumber += subGroupAnalytics.initialRoundsNumber;
        aggregated.followOnRoundsNumber += subGroupAnalytics.followOnRoundsNumber;
        aggregated.roundsNumber += subGroupAnalytics.roundsNumber;

        aggregated.initialCompaniesNumber += subGroupAnalytics.initialCompaniesNumber;
        aggregated.followOnCompaniesNumber += subGroupAnalytics.followOnCompaniesNumber;

        aggregated.initialHoldingData.weightedHoldingSum += subGroupAnalytics.initialHoldingData.weightedHoldingSum;
        aggregated.initialHoldingData.companiesNumber += subGroupAnalytics.initialHoldingData.companiesNumber;
        aggregated.exitHoldingData.weightedHoldingSum += subGroupAnalytics.exitHoldingData.weightedHoldingSum;
        aggregated.exitHoldingData.companiesNumber += subGroupAnalytics.exitHoldingData.companiesNumber;

        sb.roundsData.forEach((r, index) => {
          rInvestment += Number(r.investment);
          const companiesNumber = Number(r.companiesNumber);
          const exitCompNumber = Number(r.exitCompNumber);
          const writeOff = Number(r.writeOff);
          const proceeds = Number(r.proceeds);
          const gain = proceeds - rInvestment;

          if (exitCompNumber) {
            if (gain > 0) {
              analyticsData.atGain.updateMetrics(exitCompNumber, rInvestment, proceeds);
            } else if (gain <= 0 && r.exitCompPrice) {
              analyticsData.atLoss.updateMetrics(exitCompNumber, rInvestment, proceeds);
            } else if (!r.exitCompPrice) {
              analyticsData.writeOff.companiesNumber += exitCompNumber;
              analyticsData.writeOff.investment += rInvestment * exitCompNumber;
              analyticsData.writeOff.proceeds = 0;
              analyticsData.writeOff.gain = -analyticsData.writeOff.investment;
              analyticsData.writeOff.moic = 0;
            }
          }
          if (writeOff) {
            analyticsData.writeOff.companiesNumber += writeOff;
            analyticsData.writeOff.investment += rInvestment * writeOff;
            analyticsData.writeOff.proceeds = 0;
            analyticsData.writeOff.gain = -analyticsData.writeOff.investment;
            analyticsData.writeOff.moic = 0;
          }

          const lastRound = (index === sb.roundsData.length - 1);
          if (lastRound && (writeOff + exitCompNumber !== companiesNumber)) {
            const unallocatedCompanies = companiesNumber - (writeOff + exitCompNumber);
            analyticsData.writeOff.companiesNumber += unallocatedCompanies;
            analyticsData.writeOff.investment += rInvestment * unallocatedCompanies;
            analyticsData.writeOff.proceeds = 0;
            analyticsData.writeOff.gain = -analyticsData.writeOff.investment;
            analyticsData.writeOff.moic = 0;
          }
        });
      });
    });

    analyticsData.analyticsCards = this.assignAnalytics(aggregated);
    this.calculateGainAndMoic(analyticsData);
    this.calculateTotals(analyticsData);

    return analyticsData;
  }

  /**
   * Optimized method to calculate analytics data for a subGroup
   *
   * initialInvestment - Sum of investments in the first round through all subGroups where investment not zero.
   * followOnInvestment - Sum of all other investments after initial through all subGroups
   * totalInvestment - Sum of all investments through all subGroups
   *
   * initialRoundsNumber - number of companies in initial round in all subGroups
   * followOnRoundsNumber - number of companies in follow on rounds in all subGroups
   * roundsNumber - number of companies in all rounds in all subGroups
   *
   * initialCompaniesNumber - number of companies in initial round with investment in all subGroups
   * followOnCompaniesNumber - number of companies in FIRST follow on round with investment in all subGroups
   *
   * weightedHoldingSum - Sum(number of (exit) companies in selected round * holding in selected round) in all rounds in all subGroups
   * companiesNumber - number of (exit) companies in all rounds in all subGroups
   *
   * @param roundsData - array of rounds (already sorted)
   * @returns all calculated analytics data for the subGroup
   */
  static calculateSubGroupAnalytics(roundsData: RoundsFS[]): FsAggregatedAnalytics {
    const analytics = new FsAggregatedAnalytics();
    let isInitialRound = true;
    let isFirstFollowOnRound = false;

    if (!roundsData || roundsData.length === 0) {
      return analytics;
    }

    for (const round of roundsData) {
      // if investment exist => round is not empty
      if (round.investment) {
        const totalInv = round.totalInvestment || 0;
        const companiesNum = round.companiesNumber || 0;

        analytics.totalInvestment += totalInv;
        analytics.roundsNumber += companiesNum;

        if (isInitialRound) {
          // initial round
          analytics.initialInvestment = totalInv;
          analytics.initialRoundsNumber = companiesNum;
          analytics.initialCompaniesNumber = companiesNum; // Via Fin logic it is the same value as initial rounds number
          isInitialRound = false;
          isFirstFollowOnRound = true; // first round with investment after initial round

          // initial holding
          if (companiesNum) {
            analytics.initialHoldingData.weightedHoldingSum = companiesNum * (round.holding || 0);
            analytics.initialHoldingData.companiesNumber = companiesNum;
          }
        } else {
          // follow-on rounds
          if (isFirstFollowOnRound) {
            analytics.followOnCompaniesNumber = round.companiesNumber;
            isFirstFollowOnRound = false;
          }

          analytics.followOnInvestment += totalInv;
          analytics.followOnRoundsNumber += companiesNum;
        }
      }

      // Calculate exit holding data for any round with exit companies
      if (round.exitCompNumber && round.holding) {
        analytics.exitHoldingData.weightedHoldingSum += round.exitCompNumber * round.holding;
        analytics.exitHoldingData.companiesNumber += round.exitCompNumber;
      }
    }

    return analytics;
  }

  static assignAnalytics(aggregated: FsAggregatedAnalytics): FsAnalyticsCards {
    const analyticsCards = new FsAnalyticsCards();
    analyticsCards.initialInvestment = aggregated.initialInvestment;
    analyticsCards.followOnInvestment = aggregated.followOnInvestment;
    analyticsCards.totalInvestment = aggregated.totalInvestment;
    analyticsCards.initialRoundsNumber = aggregated.initialRoundsNumber;
    analyticsCards.followOnRoundsNumber = aggregated.followOnRoundsNumber;
    analyticsCards.roundsNumber = aggregated.roundsNumber;
    analyticsCards.avgInitialInv = this.calculateAverage(aggregated.initialInvestment, aggregated.initialRoundsNumber);
    analyticsCards.initialCompaniesNumber = aggregated.initialCompaniesNumber;
    analyticsCards.avgFollowOnInv = this.calculateAverage(aggregated.followOnInvestment, aggregated.followOnRoundsNumber);
    analyticsCards.followOnCompaniesNumber = aggregated.followOnCompaniesNumber;
    analyticsCards.avgTotalInv = this.calculateAverage(aggregated.totalInvestment, aggregated.roundsNumber);
    analyticsCards.avgInitialHolding = this.calculateAverage(aggregated.initialHoldingData.weightedHoldingSum, aggregated.initialHoldingData.companiesNumber);
    analyticsCards.avgExitHolding = this.calculateAverage(aggregated.exitHoldingData.weightedHoldingSum, aggregated.exitHoldingData.companiesNumber);
    analyticsCards.holdingDilution = analyticsCards.avgInitialHolding > 0 ? 1 - (analyticsCards.avgExitHolding / analyticsCards.avgInitialHolding) : 0;
    return analyticsCards;
  }

  static createFundMetrics(analyticsData: FsAnalyticsData, selectedModel: FundStrategyModel): FSFundMetricsDTO {
    const metrics = new FSFundMetricsDTO();
    metrics.totalCommitment = selectedModel.totalCommitment || 0;
    metrics.expectedExpenses = selectedModel.expectedExpenses || 0;
    metrics.carryInterestPcnt = (selectedModel.carryInterestPcnt || 0) * 100;
    metrics.investment = analyticsData.total.investment;
    metrics.proceeds = analyticsData.total.proceeds;
    return metrics;
  }

  private static calculateGainAndMoic(analyticsData: FsAnalyticsData): void {
    [analyticsData.atGain, analyticsData.atLoss].forEach((metric) => {
      metric.calculateGain();
      metric.calculateMoic();
    });
  }

  private static calculateTotals(analyticsData: FsAnalyticsData): void {
    analyticsData.total.companiesNumber = 0;
    analyticsData.total.investment = 0;
    analyticsData.total.proceeds = 0;
    analyticsData.total.gain = 0;
    analyticsData.total.moic = 0;

    [analyticsData.atGain, analyticsData.atLoss, analyticsData.writeOff].forEach((metric) => {
      analyticsData.total.companiesNumber += metric.companiesNumber;
      analyticsData.total.investment += metric.investment;
      analyticsData.total.proceeds += metric.proceeds;
    });

    analyticsData.total.gain = analyticsData.total.proceeds - analyticsData.total.investment;
    analyticsData.total.moic = analyticsData.total.investment ? analyticsData.total.proceeds / analyticsData.total.investment : 0;
  }

  private static calculateAverage(total: number, count: number): number {
    return count !== 0 ? total / count : 0;
  };
}
